<template>
    <div class="price-histogram">
        <datalist :id="`histogram-${id}`" class="price-histogram__graph-container">
            <option
                v-for="bucket in data"
                :key="bucket.bucketValue"
                :value="bucket.bucketValue"
                class="price-histogram__graph-container__data-bucket"
                :style="`height:${bucket.frequency * 100}%`"
            ></option>
        </datalist>
        <div class="price-histogram__range-selector">
            <div class="price-histogram__inputs">
                <!-- We use :keys containing the min/maxBound values to force the browser to re-render the components when we change the bounds. For instance, the current bounds are min=500 and max=1500 and the current value=1000. When we change the max bound to 800 then set it back to 1500 the browser misplaces the cursor. -->
                <input
                    :key="`range-a-${minBound}-${maxBound}`"
                    v-model="rangeA"
                    data-testid="histogram-range-a"
                    class="price-histogram__range-a"
                    type="range"
                    :min="minBound"
                    :max="maxBound"
                    :list="`histogram-${id}`"
                    step="10"
                    aria-label="range A"
                    @input="onInput"
                    @change="onChange"
                />
                <input
                    :key="`range-b-${minBound}-${maxBound}`"
                    v-model="rangeB"
                    data-testid="histogram-range-b"
                    class="price-histogram__range-b"
                    type="range"
                    :min="minBound"
                    :max="maxBound"
                    :list="`histogram-${id}`"
                    step="10"
                    aria-label="range B"
                    @input="onInput"
                    @change="onChange"
                />
            </div>
            <div class="price-histogram__caption">
                <div>
                    {{ selectedMinLabel }}
                </div>
                <div>
                    {{ selectedMaxLabel }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref, watch} from 'vue';

    const props = defineProps<{
        id: string;
        minBound: number;
        maxBound: number;
        data: Array<number>;
        selectedMin?: number;
        selectedMax?: number;
        selectedMinLabel?: string;
        selectedMaxLabel?: string;
    }>();

    const emit = defineEmits<{
        (e: 'input', min: number, max: number): void;
        (e: 'change', min: number, max: number): void;
    }>();

    const rangeA = ref(props.selectedMin ?? props.minBound);
    const rangeB = ref(props.selectedMax ?? props.maxBound);
    watch(
        () => [props.selectedMin, props.selectedMax, props.minBound, props.maxBound],
        ([min, max]) => {
            rangeA.value = min ?? props.minBound;
            rangeB.value = max ?? props.maxBound;
        },
        {immediate: true},
    );

    const rangeAInt = computed(() => Math.trunc(rangeA.value));
    const rangeBInt = computed(() => Math.trunc(rangeB.value));
    const rangeMin = computed(() => Math.min(rangeAInt.value, rangeBInt.value));
    const rangeMax = computed(() => Math.max(rangeAInt.value, rangeBInt.value));

    const bucketSize = computed(() => (props.maxBound - props.minBound) / props.data.length);

    const data = computed(() =>
        props.data.map((frequency, index) => ({
            bucketValue: props.minBound + bucketSize.value * index,
            frequency,
        })),
    );

    function onInput() {
        emit('input', rangeMin.value, rangeMax.value);
    }

    function onChange() {
        emit('change', rangeMin.value, rangeMax.value);
    }

    const graphUnselectedAreasOverlay = computed(() => {
        const sliderColor = 'white';
        const rangeColor = 'transparent';
        const rangeDistance = props.maxBound - props.minBound;
        const fromPosition = rangeMin.value - props.minBound;
        const toPosition = rangeMax.value - props.minBound;
        return `linear-gradient(
        to right,
        ${sliderColor} 0%,
        ${sliderColor} ${(fromPosition / rangeDistance) * 100}%,
        ${rangeColor} ${(fromPosition / rangeDistance) * 100}%,
        ${rangeColor} ${(toPosition / rangeDistance) * 100}%,
        ${sliderColor} ${(toPosition / rangeDistance) * 100}%,
        ${sliderColor} 100%)`;
    });

    const rangeUnselectedAreaOverlay = computed(() => {
        const sliderColor = 'var(--joy-color-neutral-40)';
        const rangeColor = 'var(--joy-color-secondary-90)';
        const rangeDistance = props.maxBound - props.minBound;
        const fromPosition = rangeMin.value - props.minBound;
        const toPosition = rangeMax.value - props.minBound;
        return `linear-gradient(
        to right,
        ${sliderColor} 0%,
        ${sliderColor} ${(fromPosition / rangeDistance) * 100}%,
        ${rangeColor} ${(fromPosition / rangeDistance) * 100}%,
        ${rangeColor} ${(toPosition / rangeDistance) * 100}%,
        ${sliderColor} ${(toPosition / rangeDistance) * 100}%,
        ${sliderColor} 100%)`;
    });
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint' as joy;

    @mixin price-histogram-slider-thumb {
        -webkit-appearance: none;
        pointer-events: all;
        touch-action: none;
        width: var(--joy-core-spacing-4);
        height: var(--joy-core-spacing-4);
        background-color: var(--joy-color-neutral-10);
        border: 2px solid var(--joy-color-secondary-90);
        border-radius: var(--joy-core-radius-6);
        box-shadow: var(--joy-core-elevation-1);
        cursor: pointer;
        transition: transform var(--joy-transition-duration-default) var(--joy-transition-timing-function);

        @include joy.screen_sm {
            width: var(--joy-core-spacing-5);
            height: var(--joy-core-spacing-5);
        }
    }

    .price-histogram {
        &__graph-container {
            position: relative;
            display: flex;
            justify-content: stretch;
            align-items: flex-end;
            height: 35px;
            margin: 0 auto;
            padding: 0 var(--joy-core-spacing-2);

            &:before {
                content: '';
                position: absolute;
                height: 100%;
                width: 100%;
                background: v-bind(graphUnselectedAreasOverlay);
                filter: opacity(0.5);
            }

            &__data-bucket {
                background-color: var(--joy-color-secondary-50);
                width: 100%;
                min-height: 0;
            }
        }

        &__range-selector {
            --range-thumb-width: var(--joy-core-spacing-3);
            display: flex;
            flex-direction: column;
            margin: 0 auto;
        }

        &__inputs {
            position: relative;
            min-height: var(--joy-core-spacing-6);
        }

        &__caption {
            position: relative;
            display: flex;
            justify-content: space-between;
        }
    }

    input[type='range']::-webkit-slider-thumb:hover {
        transform: scale(1.2);
    }

    input[type='range'] {
        display: flex;
        align-items: center;
        -webkit-appearance: none;
        appearance: none;
        height: 2px;
        width: 100%;
        position: absolute;
        background-color: #c6c6c6;
        pointer-events: none;

        &::-webkit-slider-thumb {
            @include price-histogram-slider-thumb;
        }

        &::-moz-range-thumb {
            @include price-histogram-slider-thumb;
        }

        &.price-histogram__range-a {
            height: 0;
            z-index: 1;
        }

        &.price-histogram__range-b {
            background: v-bind(rangeUnselectedAreaOverlay);
        }
    }
</style>
